import { useMemo, useState } from "react";
import {
  useAddCommitteeMemberMutation,
  useCreateCommitteePersonMutation,
  useCreateCommitteeYearMutation,
  useDeleteCommitteeMemberMutation,
  useDeleteCommitteeYearMutation,
  useGetAdminCommitteePeopleQuery,
  useGetAdminCommitteeYearsQuery,
  useUpdateCommitteeMemberMutation,
  useUpdateCommitteePersonMutation,
} from "../../features/api/appApi";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminField,
  AdminFormGrid,
  AdminInput,
  AdminScrollTable,
  AdminSelect,
  AdminStatusBadge,
  adminCn,
} from "./AdminSectionCard";

const badgeOptions = [
  "moderator",
  "deputy-moderator",
  "president",
  "vice-president",
  "secretary",
  "treasurer",
  "organizer",
  "event",
  "outreach",
  "information",
  "public relation",
  "social media",
  "graphics",
  "photography",
  "developer",
  "female mentor",
  "executive",
];

const defaultPersonForm = {
  name: "",
  category: "student",
  studentId: "",
  image: "",
  email: "",
  facebook: "",
  github: "",
  linkedin: "",
  designation: "",
};

const defaultMemberForm = {
  yearId: "",
  committeePersonId: "",
  position: "",
  badge: "",
  sortOrder: 0,
};

const AdminCommitteePage = () => {
  const { data: committeeYears = [] } = useGetAdminCommitteeYearsQuery();
  const { data: committeePeople = [] } = useGetAdminCommitteePeopleQuery();
  const [createCommitteePerson] = useCreateCommitteePersonMutation();
  const [updateCommitteePerson] = useUpdateCommitteePersonMutation();
  const [createCommitteeYear] = useCreateCommitteeYearMutation();
  const [addCommitteeMember] = useAddCommitteeMemberMutation();
  const [updateCommitteeMember] = useUpdateCommitteeMemberMutation();
  const [deleteCommitteeMember] = useDeleteCommitteeMemberMutation();
  const [deleteCommitteeYear] = useDeleteCommitteeYearMutation();

  const [editingPersonId, setEditingPersonId] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);

  const [personForm, setPersonForm] = useState(defaultPersonForm);
  const [yearForm, setYearForm] = useState({ year: "", label: "" });
  const [memberForm, setMemberForm] = useState(defaultMemberForm);

  const yearOptions = useMemo(
    () => committeeYears.map((y) => ({ value: y.id, label: y.label || y.year })),
    [committeeYears]
  );

  return (
    <AdminSectionCard title="Committee" subtitle="Create people, years, and assign members">
      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          if (editingPersonId) {
            await updateCommitteePerson({ personId: editingPersonId, ...personForm });
          } else {
            await createCommitteePerson(personForm);
          }
          setEditingPersonId(null);
          setPersonForm(defaultPersonForm);
        }}
      >
        <AdminFormGrid>
          <AdminField label="Person name">
            <AdminInput
              placeholder="Person name"
              value={personForm.name}
              onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Category">
            <AdminSelect
              value={personForm.category}
              onChange={(e) => setPersonForm({ ...personForm, category: e.target.value })}
            >
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
            </AdminSelect>
          </AdminField>

          <AdminField label="Student ID">
            <AdminInput
              placeholder="Student ID"
              value={personForm.studentId}
              onChange={(e) => setPersonForm({ ...personForm, studentId: e.target.value })}
            />
          </AdminField>

          <AdminField label="Designation">
            <AdminInput
              placeholder="Designation"
              value={personForm.designation}
              onChange={(e) => setPersonForm({ ...personForm, designation: e.target.value })}
            />
          </AdminField>

          <AdminField label="Image URL" className="md:col-span-2">
            <AdminInput
              placeholder="Image URL"
              value={personForm.image}
              onChange={(e) => setPersonForm({ ...personForm, image: e.target.value })}
            />
          </AdminField>

          <AdminField label="Facebook URL">
            <AdminInput
              placeholder="Facebook URL"
              value={personForm.facebook}
              onChange={(e) => setPersonForm({ ...personForm, facebook: e.target.value })}
            />
          </AdminField>

          <AdminField label="Email">
            <AdminInput
              placeholder="Email"
              value={personForm.email}
              onChange={(e) => setPersonForm({ ...personForm, email: e.target.value })}
            />
          </AdminField>

          <AdminField label="GitHub URL">
            <AdminInput
              placeholder="GitHub URL"
              value={personForm.github}
              onChange={(e) => setPersonForm({ ...personForm, github: e.target.value })}
            />
          </AdminField>

          <AdminField label="LinkedIn URL">
            <AdminInput
              placeholder="LinkedIn URL"
              value={personForm.linkedin}
              onChange={(e) => setPersonForm({ ...personForm, linkedin: e.target.value })}
            />
          </AdminField>
        </AdminFormGrid>

        <div className="flex flex-wrap gap-2">
          <AdminButton type="submit">{editingPersonId ? "Update Person" : "Create Person"}</AdminButton>
          {editingPersonId ? (
            <AdminButton
              variant="ghost"
              type="button"
              onClick={() => {
                setEditingPersonId(null);
                setPersonForm(defaultPersonForm);
              }}
            >
              Cancel Edit
            </AdminButton>
          ) : null}
        </div>
      </form>

      <div className="mt-5">
        {committeePeople.length ? (
          <AdminScrollTable>
            <table className="w-full min-w-[700px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Student ID</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {committeePeople.map((person) => (
                  <tr key={person.id} className="bg-white hover:bg-slate-50/70">
                    <td className="max-w-[250px] break-words px-4 py-3 font-semibold text-slate-800">
                      {person.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{person.category || person.type || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{person.studentId || person.identifier || "-"}</td>
                    <td className="max-w-[220px] break-words px-4 py-3 text-slate-600">{person.designation || "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <AdminButton
                        className="h-9 bg-indigo-600 px-3 text-xs text-white hover:bg-indigo-700"
                        onClick={() => {
                          setEditingPersonId(person.id);
                          setPersonForm({
                            name: person.name || "",
                            category: person.category || person.type || "student",
                            studentId: person.studentId || person.identifier || "",
                            image: person.image || person.imageUrl || "",
                            email: person.email || "",
                            facebook: person.facebook || "",
                            github: person.github || "",
                            linkedin: person.linkedin || "",
                            designation: person.designation || "",
                          });
                        }}
                      >
                        Edit
                      </AdminButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminScrollTable>
        ) : (
          <AdminEmptyState text="No committee people found." />
        )}
      </div>

      <form
        className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          await createCommitteeYear({ year: Number(yearForm.year), label: yearForm.label });
          setYearForm({ year: "", label: "" });
        }}
      >
        <p className="text-sm font-semibold text-slate-700">Create Committee Year</p>
        <AdminFormGrid>
          <AdminField label="Year">
            <AdminInput
              placeholder="Year"
              value={yearForm.year}
              onChange={(e) => setYearForm({ ...yearForm, year: e.target.value })}
              required
            />
          </AdminField>
          <AdminField label="Label">
            <AdminInput
              placeholder="Label"
              value={yearForm.label}
              onChange={(e) => setYearForm({ ...yearForm, label: e.target.value })}
            />
          </AdminField>
        </AdminFormGrid>
        <AdminButton type="submit">Create Year</AdminButton>
      </form>

      <form
        className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (editingMemberId) {
            await updateCommitteeMember({
              memberId: editingMemberId,
              position: memberForm.position,
              badge: memberForm.badge || null,
              sortOrder: Number(memberForm.sortOrder),
            });
          } else {
            await addCommitteeMember({
              ...memberForm,
              yearId: Number(memberForm.yearId),
              committeePersonId: Number(memberForm.committeePersonId),
              sortOrder: Number(memberForm.sortOrder),
            });
          }
          setEditingMemberId(null);
          setMemberForm(defaultMemberForm);
        }}
      >
        <p className="text-sm font-semibold text-slate-700">Add or Update Member</p>
        <AdminFormGrid>
          <AdminField label="Year">
            <AdminSelect
              value={memberForm.yearId}
              onChange={(e) => setMemberForm({ ...memberForm, yearId: e.target.value })}
              required
            >
              <option value="">Select year</option>
              {yearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </AdminSelect>
          </AdminField>

          <AdminField label="Person">
            <AdminSelect
              value={memberForm.committeePersonId}
              onChange={(e) => setMemberForm({ ...memberForm, committeePersonId: e.target.value })}
              required
            >
              <option value="">Select person</option>
              {committeePeople.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.category || p.type})
                </option>
              ))}
            </AdminSelect>
          </AdminField>

          <AdminField label="Position">
            <AdminInput
              placeholder="Position"
              value={memberForm.position}
              onChange={(e) => setMemberForm({ ...memberForm, position: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Badge">
            <AdminSelect
              value={memberForm.badge}
              onChange={(e) => setMemberForm({ ...memberForm, badge: e.target.value })}
            >
              <option value="">Select badge</option>
              {badgeOptions.map((badge) => (
                <option key={badge} value={badge}>
                  {badge}
                </option>
              ))}
            </AdminSelect>
          </AdminField>

          <AdminField label="Sort order">
            <AdminInput
              type="number"
              placeholder="Sort order"
              value={memberForm.sortOrder}
              onChange={(e) => setMemberForm({ ...memberForm, sortOrder: e.target.value })}
            />
          </AdminField>
        </AdminFormGrid>

        <div className="flex flex-wrap gap-2">
          <AdminButton type="submit">{editingMemberId ? "Update Member" : "Add Member"}</AdminButton>
          {editingMemberId ? (
            <AdminButton
              variant="ghost"
              type="button"
              onClick={() => {
                setEditingMemberId(null);
                setMemberForm(defaultMemberForm);
              }}
            >
              Cancel Member Edit
            </AdminButton>
          ) : null}
        </div>
      </form>

      <div className="mt-5 space-y-4">
        {committeeYears.length ? (
          committeeYears.map((year) => (
            <div key={year.id} className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-800">{year.label || year.year}</p>
                  <AdminStatusBadge tone="slate">{(year.members || []).length} members</AdminStatusBadge>
                </div>
                <AdminButton
                  variant="danger"
                  className="h-9 px-3 text-xs"
                  type="button"
                  onClick={() => deleteCommitteeYear(year.id)}
                >
                  Delete Year
                </AdminButton>
              </div>

              {(year.members || []).length ? (
                <AdminScrollTable>
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="bg-white text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Position</th>
                        <th className="px-4 py-3">Badge</th>
                        <th className="px-4 py-3">Student ID</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(year.members || []).map((member) => (
                        <tr key={member.id} className="bg-white hover:bg-slate-50/70">
                          <td className="max-w-[220px] break-words px-4 py-3 font-semibold text-slate-800">
                            {member.person?.name || "Unknown"}
                          </td>
                          <td className="max-w-[220px] break-words px-4 py-3 text-slate-600">{member.position}</td>
                          <td className="px-4 py-3 text-slate-600">{member.badge || "-"}</td>
                          <td className="px-4 py-3 text-slate-600">{member.person?.studentId || "-"}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                className={adminCn(
                                  "inline-flex h-9 cursor-pointer items-center justify-center rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white transition hover:bg-indigo-700"
                                )}
                                type="button"
                                onClick={() => {
                                  setEditingMemberId(member.id);
                                  setMemberForm({
                                    yearId: String(year.id),
                                    committeePersonId: String(member.committeePersonId),
                                    position: member.position || "",
                                    badge: member.badge || "",
                                    sortOrder: Number(member.sortOrder || 0),
                                  });
                                }}
                              >
                                Edit
                              </button>

                              <AdminButton
                                variant="danger"
                                className="h-9 px-3 text-xs"
                                type="button"
                                onClick={() => deleteCommitteeMember(member.id)}
                              >
                                Remove
                              </AdminButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AdminScrollTable>
              ) : (
                <div className="p-4">
                  <AdminEmptyState text="No members assigned for this year yet." />
                </div>
              )}
            </div>
          ))
        ) : (
          <AdminEmptyState text="No committee years created yet." />
        )}
      </div>
    </AdminSectionCard>
  );
};

export default AdminCommitteePage;
